import React, { useContext, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Style from "./PaginaSaqueStyle";
import { AuthContext } from "../../context/AuthContext";
import { formatarMoeda, formatDateSystem } from "../../assets/utils";
import {
  FiArrowLeft,
  FiAlertCircle,
  FiClock,
  FiFileText,
  FiTrendingDown,
  FiDollarSign,
  FiCheckCircle,
  FiUpload, // Importado
  FiDownload, // Importado
} from "react-icons/fi";
import { useMessage } from "../../context/MessageContext";
import { withdrawService } from "../../Services/withdrawService";

const PaginaSaque = () => {
  const { saqueId } = useParams();
  const navigate = useNavigate();
  const { withdraws, userData, reloadUserData } = useContext(AuthContext);
  const { showMessage } = useMessage();

  const [saque, setSaque] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showReceiptModal, setShowReceiptModal] = useState(false); // Estado do modal

  useEffect(() => {
    const findSaque = withdraws?.find((s) => s.id.toString() === saqueId);
    if (findSaque) {
      setSaque(findSaque);
    }
    setIsLoading(false);
  }, [saqueId, withdraws]);

  const handleCancelSaque = async () => {
    if (
      !window.confirm(
        "Você tem certeza que deseja cancelar este saque? Esta ação não pode ser desfeita."
      )
    )
      return;

    try {
      await withdrawService.cancelWithdrawal(saque.id, userData.id);
      showMessage("Saque cancelado com sucesso!", "success");
      await reloadUserData();
      navigate(-1);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Erro ao cancelar o saque.";
      showMessage(errorMessage, "error");
      console.error("Erro ao cancelar o saque:", error);
    }
  };

  // Função chamada após sucesso no upload
  const handleReceiptSuccess = async () => {
    await reloadUserData();
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 1:
        return { text: "Pendente", icon: <FiClock /> };
      case 2:
        return { text: "Pago", icon: <FiCheckCircle /> };
      case 3:
        return { text: "Cancelado", icon: <FiAlertCircle /> };
      case 4:
        return { text: "Ctr. Recomprado", icon: <FiAlertCircle /> };
      default:
        return { text: "Desconhecido", icon: <FiAlertCircle /> };
    }
  };

  if (isLoading) {
    return (
      <Style.LoadingContainer>
        Carregando detalhes do saque...
      </Style.LoadingContainer>
    );
  }

  if (!saque) {
    return (
      <Style.LoadingContainer>Saque não encontrado.</Style.LoadingContainer>
    );
  }

  const valorBruto = saque.amountWithdrawn;
  const taxa = valorBruto * 0.04;
  const valorLiquido = saque.amountReceivable;
  const statusInfo = getStatusInfo(saque.status);

  return (
    <Style.PaginaContainer>
      {/* Modal de Recibo (Reutiliza o modal criado anteriormente, 
                mas precisaria de ajuste se o modal só aceitasse contractId. 
                Assumindo que vamos criar/adaptar o modal para aceitar withdrawId também) 
            */}
      {/* 
               NOTA: Se o CreateReceiptModal atual só aceita contractId, você precisará atualizá-lo 
               para aceitar withdrawId ou criar um CreateWithdrawReceiptModal.
               Vou assumir que você vai adaptar o modal para aceitar a prop `type="withdraw"` ou `withdrawId`.
            */}
      {/* 
               <CreateReceiptModal 
                  withdrawId={saque.id} 
                  clientId={saque.clientId} 
                  contractId={saque.contractId} // Se houver
                  type="withdraw" // Passando o tipo para o modal saber qual endpoint chamar
                  onClose={() => setShowReceiptModal(false)}
                  onSuccess={handleReceiptSuccess}
               />
            */}

      <Style.ContentWrapper>
        <Style.Header>
          <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
            <Style.BackButton onClick={() => navigate(-1)} title="Voltar">
              <FiArrowLeft />
            </Style.BackButton>
            <Style.Title>
              Detalhes <span>#{saque.id}</span>
            </Style.Title>
          </div>

          {/* Botão de Recibo no Header */}
          {saque.receiptUrl ? (
            <Style.ReceiptButton
              href={saque.receiptUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FiFileText /> Ver Comprovante
            </Style.ReceiptButton>
          ) : (
            // Se não tiver recibo, mostra botão de upload (se fizer sentido para sua regra de negócio)
            // Geralmente saque = comprovante de pagamento da plataforma pro cliente.
            // Se o cliente não pode upar, remova esse bloco `else`.
            <div />
          )}
        </Style.Header>

        <Style.DetailsCard>
          <Style.CardBody>
            <Style.InfoItem>
              <Style.InfoLabel>
                <FiAlertCircle />
                Status
              </Style.InfoLabel>
              <Style.InfoValue>
                <Style.StatusBadge status={saque.status}>
                  {statusInfo.icon} {statusInfo.text}
                </Style.StatusBadge>
              </Style.InfoValue>
            </Style.InfoItem>
            <Style.InfoItem>
              <Style.InfoLabel>
                <FiClock />
                Data da Solicitação
              </Style.InfoLabel>
              <Style.InfoValue>
                {formatDateSystem(saque.dateCreated)}
              </Style.InfoValue>
            </Style.InfoItem>
            <Style.InfoItem>
              <Style.InfoLabel>
                <FiFileText />
                Descrição
              </Style.InfoLabel>
              <Style.InfoValue>{saque.description}</Style.InfoValue>
            </Style.InfoItem>
            <Style.InfoItem>
              <Style.InfoLabel>
                <FiDollarSign />
                Valor Bruto
              </Style.InfoLabel>
              <Style.InfoValue>R$ {formatarMoeda(valorBruto)}</Style.InfoValue>
            </Style.InfoItem>
            <Style.InfoItem>
              <Style.InfoLabel>
                <FiTrendingDown />
                Taxa (4%)
              </Style.InfoLabel>
              <Style.InfoValue>- R$ {formatarMoeda(taxa)}</Style.InfoValue>
            </Style.InfoItem>
            <Style.InfoItem>
              <Style.InfoLabel>
                <FiCheckCircle />
                Valor a Receber
              </Style.InfoLabel>
              <Style.InfoValue className="highlight">
                R$ {formatarMoeda(valorLiquido)}
              </Style.InfoValue>
            </Style.InfoItem>
          </Style.CardBody>

          <Style.CardFooter>
            {saque.status === 1 && (
              <Style.ActionButton onClick={handleCancelSaque}>
                Cancelar Saque
              </Style.ActionButton>
            )}

            {/* Botão extra de download na parte inferior se já tiver recibo */}
            {saque.receiptUrl && (
              <Style.SecondaryButton
                href={saque.receiptUrl}
                download
                target="_blank"
              >
                <FiDownload /> Baixar Comprovante
              </Style.SecondaryButton>
            )}
          </Style.CardFooter>
        </Style.DetailsCard>
      </Style.ContentWrapper>
    </Style.PaginaContainer>
  );
};

export default PaginaSaque;
